import React, { useState } from 'react';
import {
  parseMandantNr,
  resolveCoachInitialLetter,
} from '../utils/mandantNrFormat';

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Zerlegt Anzeige in Kern `MA - NN/YYYY` + Betreuer-Buchstabe (PeBase-Stil). */
export function splitMandantNrForPill(
  displayNr: string,
  betreuer?: string | null,
  team?: any[]
): { base: string; letter: string } {
  const raw = String(displayNr || '').trim();
  if (!raw) return { base: '', letter: '' };

  const parsed = parseMandantNr(raw);
  let letter = '';
  if (parsed.coachInitial && ['C', 'M', 'J', 'N'].includes(parsed.coachInitial)) {
    letter = parsed.coachInitial;
  } else {
    const fromSuffix = raw.match(/(?:^|[\s-])([CMJN])\s*$/i);
    if (fromSuffix) letter = fromSuffix[1].toUpperCase();
  }
  if (!letter) {
    const fromBetreuer = resolveCoachInitialLetter(betreuer, team);
    if (fromBetreuer && ['C', 'M', 'J', 'N'].includes(fromBetreuer)) {
      letter = fromBetreuer;
    }
  }

  let base = raw;
  if (parsed.core && /^\d{2}\/\d{4}$/.test(parsed.core)) {
    base = `MA - ${parsed.core}`;
  } else {
    base = raw
      .replace(/\s*-\s*[CMJN]\s*$/i, '')
      .replace(/-([CMJN])\s*$/i, '')
      .replace(/\s+(Solo|TeamBase|TeamPro\+|TeamPro)\s*$/i, '')
      .trim();
  }

  return { base, letter };
}

export interface MandantNrPillProps {
  displayNr: string;
  betreuer?: string | null;
  team?: any[];
  /** Tarif-Status (Solo/TeamBase/TeamPro…) — goldener Badge innerhalb der blauen Umrandung */
  tarifLabel?: string | null;
  /** Original-Badge-Klassen (z. B. aus resolveTarifBadgeDisplay) */
  tarifClassName?: string | null;
  /** Volltext für Zwischenablage (Default: base + Letter, ohne Tarif). */
  copyValue?: string;
  showNotification?: (msg: string, type?: 'success' | 'error') => void;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * PeBase-Stil: [Copy] MA - NN/YYYY [Betreuer] [Tarif-Badge] — alles in einer blauen Umrandung
 */
export const MandantNrPill: React.FC<MandantNrPillProps> = ({
  displayNr,
  betreuer,
  team,
  tarifLabel,
  tarifClassName,
  copyValue,
  showNotification,
  className = '',
  size = 'md',
}) => {
  const [copied, setCopied] = useState(false);
  const { base, letter } = splitMandantNrForPill(displayNr, betreuer, team);
  const shown = base || displayNr || '—';
  const tarif = String(tarifLabel || '').trim();
  const clipboard =
    (copyValue && copyValue.trim()) ||
    (letter ? `${shown} - ${letter}` : shown);
  const canCopy = Boolean(clipboard && clipboard !== '---' && clipboard !== '—');

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(clipboard);
      setCopied(true);
      showNotification?.('Kopiert!', 'success');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      showNotification?.('Kopieren fehlgeschlagen', 'error');
    }
  };

  const pad = size === 'sm' ? 'px-2 py-1 gap-1.5' : 'px-2.5 py-1.5 gap-2';
  const textSize = size === 'sm' ? 'text-sm' : 'text-base';
  const badgeSize = size === 'sm' ? 'w-6 h-6 text-[11px]' : 'w-7 h-7 text-xs';
  const tarifBadgeCls =
    String(tarifClassName || '').trim() ||
    'px-2 py-1 rounded text-[9px] font-bold tracking-widest border bg-[#E6C299]/45 text-[#5C3D1F] border-[#8D6435]/55 dark:bg-[#E6C299]/10 dark:text-[#E6C299] dark:border-[#E6C299]/30';

  return (
    <span
      className={`inline-flex items-center ${pad} rounded-md border border-sky-600/45 bg-sky-100/90 text-sky-800 dark:border-sky-500/50 dark:bg-sky-950/50 dark:text-sky-300 ${className}`}
    >
      {canCopy && (
        <button
          type="button"
          onClick={handleCopy}
          title="In Zwischenablage kopieren"
          className="shrink-0 inline-flex items-center justify-center text-sky-700 hover:text-sky-950 dark:text-sky-400 dark:hover:text-sky-200 transition-colors"
          aria-label="Mandant-Nummer kopieren"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      )}
      <span
        className={`${textSize} font-bold font-mono-numbers tracking-tight leading-none select-text whitespace-nowrap`}
      >
        {shown}
      </span>
      {letter ? (
        <span
          title={`Betreuer: ${letter}`}
          className={`${badgeSize} shrink-0 inline-flex items-center justify-center rounded border border-[#8D6435]/70 text-[#8D6435] dark:border-[#E6C299]/70 dark:text-[#E6C299] font-black leading-none`}
        >
          {letter}
        </span>
      ) : null}
      {tarif ? (
        <span title={`Tarif: ${tarif}`} className={`shrink-0 ${tarifBadgeCls}`}>
          {tarif}
        </span>
      ) : null}
    </span>
  );
};

export default MandantNrPill;
